import { setGtmUser, clearGtmUser, type ShiOneIdentity } from './identity';
import { markIdentityResolved } from './identityReadiness';
import { parseCrmReportingBuPath } from '../../utils/parseCrmReportingBuPath';

/**
 * Sentinel `user_id` for virtual-admin sessions - distinguishable from a real
 * customer user id and non-PII. Virtual admins aren't real people, so this replaces
 * using their email as the identifier.
 */
export const VIRTUAL_ADMIN_USER_ID_SENTINEL = '-1';

/**
 * Input to {@link setShiOneIdentity}. Extends {@link ShiOneIdentity} with the
 * guest/virtual-admin policy inputs that decide what `userId` resolves to, rather
 * than requiring every caller to re-derive the sentinel/omission rule.
 */
export interface ShiOneIdentityInput extends Omit<ShiOneIdentity, 'userId' | keyof SalesOrgIdentity> {
  userId?: string;
  isGuestUser?: boolean;
  salesOrg?: SalesOrgInput;
  isComplete?: boolean;
}

/** Mirrors the backend's `SalesOrgAttributionStatus`, which CX receives via claims. */
export const SalesOrgStatus = {
  Resolved: 'resolved',
  NoAe: 'no_ae',
  AeUnmatched: 'ae_unmatched',
  NoBu: 'no_bu',
  NoAccount: 'no_account',
  LookupFailed: 'lookup_failed',
} as const;

/**
 * Sales-org attribution. CX: the effective primary AE of the signed-in customer's
 * account, that AE's reporting BU path, and the account owning team's sales key, all from
 * claims. CS: the effective employee's own reporting BU path only (no AE or sales key).
 */
export interface SalesOrgInput {
  status?: string;
  aeId?: string;
  reportingBuId?: string;
  reportingBuPath?: string;
  salesKey?: string;
}

type SalesOrgIdentity = Pick<
  ShiOneIdentity,
  | 'aeId'
  | 'salesKey'
  | 'salesOrgStatus'
  | 'reportingBuId'
  | 'division'
  | 'divisionSegment'
  | 'regionName'
  | 'districtName'
>;

/**
 * Maps the AE's reporting BU path onto the epic's sales-org fields using the existing
 * `parseCrmReportingBuPath` convention (Segment, Division, Region, District by position).
 * The epic's `RegionDivision` isn't sent: nothing in the codebase distinguishes it from
 * `Division`.
 */
function resolveSalesOrg(salesOrg: SalesOrgInput | undefined): SalesOrgIdentity {
  const path = parseCrmReportingBuPath(salesOrg?.reportingBuPath);
  return {
    aeId: salesOrg?.aeId,
    salesKey: salesOrg?.salesKey,
    salesOrgStatus: salesOrg?.status,
    reportingBuId: salesOrg?.reportingBuId,
    division: path.division ?? undefined,
    divisionSegment: path.segment ?? undefined,
    regionName: path.region ?? undefined,
    districtName: path.district ?? undefined,
  };
}

function resolveUserId(input: ShiOneIdentityInput): string | undefined {
  if (input.isVirtualAdmin) return VIRTUAL_ADMIN_USER_ID_SENTINEL;
  if (input.isGuestUser) return undefined;
  return input.userId;
}

/**
 * The single entry point for pushing usage-telemetry identity. Resolves the
 * guest/virtual-admin `user_id` policy and forwards the rest to the low-level
 * {@link setGtmUser} primitive.
 *
 * Call again whenever identity changes mid-session - a CX account switch, a CS
 * effective-user resolution - so the dataLayer always reflects the latest identity
 * rather than whatever loaded first. Each call is one full re-registration, not a merge.
 */
export function setShiOneIdentity(input: ShiOneIdentityInput): void {
  const { salesOrg, isComplete = true, ...identity } = input;
  setGtmUser({
    ...identity,
    ...resolveSalesOrg(salesOrg),
    userId: resolveUserId(input),
  });
  if (isComplete) {
    markIdentityResolved();
  }
}

/**
 * Clears usage-telemetry identity (call on logout / effective-user sign-out, or between
 * re-registrations). Re-export of {@link clearGtmUser} so callers only need one import
 * for the full identity lifecycle. Does not release GTM loading.
 */
export { clearGtmUser as clearShiOneIdentity };

/**
 * Declares that this session definitively has no signed-in user (e.g. a public page after
 * auth has finished loading), clearing identity and releasing `GtmProvider` so anonymous
 * page views are still tracked. Call only when absence is certain - never as a cleanup step
 * between re-registrations, which would load GTM before identity is complete.
 */
export function markShiOneIdentityAbsent(): void {
  clearGtmUser();
  markIdentityResolved();
}
